import React, { useState, useEffect } from 'react';
import '../styles/Modal.css';
import { Mine } from '../pages/HomePage';

interface MinePopupProps {
  data: Mine;
  onClose: () => void;
  onEdit?: () => void;
}

/**
 * MinePopup - A modal component for displaying detailed mine information
 * Features image carousel, location details, description and custom links
 */
function MinePopup({ data, onClose, onEdit }: MinePopupProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenSlide, setFullscreenSlide] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Default placeholder images if no custom images are provided
  const images = data.images && data.images.length > 0 
    ? data.images.map(img => img.src)
    : [];

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Touch handlers for swipe gestures
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      if (isFullscreen) {
        nextFullscreenSlide();
      } else {
        nextSlide();
      }
    }
    if (isRightSwipe) {
      if (isFullscreen) {
        previousFullscreenSlide();
      } else {
        previousSlide();
      }
    }
  };

  // Handle escape key for fullscreen mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isFullscreen) {
          setIsFullscreen(false);
        }
      } else if (e.key === 'ArrowLeft' && isFullscreen) {
        previousFullscreenSlide();
      } else if (e.key === 'ArrowRight' && isFullscreen) {
        nextFullscreenSlide();
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isFullscreen]);

  // Handle popup closing with animation
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match CSS animation duration
  };

  // Carousel navigation functions
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
  };

  const previousSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Fullscreen navigation functions
  const nextFullscreenSlide = () => {
    setFullscreenSlide((prev) => (prev + 1) % images.length);
  };

  const previousFullscreenSlide = () => {
    setFullscreenSlide((prev) => (prev - 1 + images.length) % images.length);
  };

  // Open fullscreen view
  const openFullscreen = (index: number) => {
    setFullscreenSlide(index);
    setIsFullscreen(true);
  };

  // Close fullscreen view
  const closeFullscreen = () => {
    setIsFullscreen(false);
  };

  // Close popup when clicking outside the modal content
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Only show links section if custom links are provided
  const linksToShow = data.links && data.links.length > 0 ? data.links : null;

  return (
    <>
      <div className="modal-overlay" onClick={handleOverlayClick}>
        <div className={`modal ${isClosing ? 'closing' : ''}`}>
          {/* Header with title and close button */}
          <div className="modal-header">
            <h2 className="modal-title">{data.title}</h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              {onEdit && (
                <button 
                  className="btn btn-secondary" 
                  onClick={() => { onEdit(); handleClose(); }}
                  style={{ padding: '4px 8px', fontSize: '12px' }}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '14px', height: '14px' }}>
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                  </svg>
                  Upravit
                </button>
              )}
              <button className="close-btn" onClick={handleClose}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
          </div>
          
          {/* Scrollable content area */}
          <div className="modal-body">
            <div className="modal-content">
              {/* Image carousel section */}
              {images.length > 0 && (
                <div 
                  className="carousel-container"
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                >
                  <div 
                    className="carousel" 
                    style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                  >
                    {images.map((image, index) => (
                      <div 
                        key={index}
                        className="carousel-slide" 
                        style={{ backgroundImage: `url(${image})` }}
                        onClick={() => openFullscreen(index)}
                        title="Klikněte pro zvětšení obrázku"
                      />
                    ))}
                  </div>
                  
                  {/* Carousel navigation arrows */}
                  <button className="carousel-arrow prev" onClick={previousSlide}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                    </svg>
                  </button>
                  <button className="carousel-arrow next" onClick={nextSlide}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                    </svg>
                  </button>
                  
                  {/* Carousel dots indicator */}
                  <div className="carousel-nav">
                    {images.map((_, index) => (
                      <div 
                        key={index}
                        className={`carousel-dot ${index === currentSlide ? 'active' : ''}`}
                        onClick={() => goToSlide(index)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Location coordinates display */}
              <div className="mine-location">
                <svg className="location-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
                <span>
                  Souřadnice: 
                  <span className="coordinates">
                    {Number(data.position[0]).toFixed(6)}, {Number(data.position[1]).toFixed(6)}
                  </span>
                </span>
              </div>
              
              {/* Mine description */}
              <div className="mine-description">
                <strong>Popis dolu:</strong><br/><br/>
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {data.content}
                </div>
              </div>
              
              {/* Conditionally render links section if custom links exist */}
              { linksToShow && (
              <><div className="section-title">Užitečné odkazy</div><div className="mine-links">
                  {linksToShow.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      className="link-item"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="link-text">{link.title}</span>
                    </a>
                  ))}
                </div></>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen image modal */}
      {isFullscreen && (
        <div 
          className="fullscreen-overlay" 
          onClick={closeFullscreen}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="fullscreen-content" onClick={(e) => e.stopPropagation()}>
            <img 
              src={images[fullscreenSlide]} 
              alt={`${data.title} - obrázek ${fullscreenSlide + 1}`}
              className="fullscreen-image"
            />
            
            <button className="fullscreen-close" onClick={closeFullscreen}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
            
            {images.length > 1 && (
              <>
                <button className="fullscreen-nav prev" onClick={previousFullscreenSlide}>
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                  </svg>
                </button>
                <button className="fullscreen-nav next" onClick={nextFullscreenSlide}>
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                  </svg>
                </button>
                
                <div className="fullscreen-counter">
                  {fullscreenSlide + 1} / {images.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default MinePopup;
